import { IShow, IEvent } from "@eagerdog/interfaces";
import { getRunningTimeMinutes, Constants, akcConformationClassMatches } from "@eagerdog/constants";

import { ringStartOffsetMinutes } from "./judge-schedule-timeline.util";

// Helper function to get duration per entry based on hierarchy
function getDurationPerEntry(
  event: IEvent,
  show: IShow,
  dogClass: any,
  customMinutesPerEntry: number
): number {
  // 1. Custom configured/saved on the judge schedule tab (per entry)
  if (customMinutesPerEntry > 0) {
    return customMinutesPerEntry;
  }
  
  // 2. From element running times
  const sanctioningClub = event.sanctioning_club;
  const showType = show.show_type;
  const element = dogClass.show_element; // Important for scentwork!
  const level = dogClass.level;
  
  const runningTime = getRunningTimeMinutes(sanctioningClub, showType, element, level);
  if (runningTime > 0) {
    return runningTime;
  }
  
  // 3. Fallback to 0
  return 0;
}

/**
 * Utility functions for judge schedule calculations
 * These functions ensure consistency between the Judge Schedule tab and exports
 */

export interface IJudgeScheduleEntry {
  judgeName: string;
  startTime: string;
  dogClasses: {
    level: string;
    entries: number;
    totalMinutes: number;
    maleCount?: number;
    femaleCount?: number;
    label?: string; // For grouped classes like "Utility B/Preferred Utility"
    group_with?: string; // For grouped child classes
    show_element?: string; // For scentwork (Container, Interior, etc.)
    section?: string; // For C-WAGS Obedience/Rally and UKC Nosework
    isBreak?: boolean; // Flag to identify breaks
  }[];
  totalEntries: number;
  totalMinutes: number; // Includes breaks - used for calculating start times
  judgingMinutes: number; // Excludes breaks - used for capacity and display
}

/**
 * Extract judge schedule data from the rings array
 * This ensures the export uses the same calculated data as the Judge Schedule tab
 */
export function extractJudgeScheduleFromRings(
  show: IShow, 
  ringName?: string,
  scores?: any[],
  event?: IEvent
): IJudgeScheduleEntry[] {
  const targetRings = ringName 
    ? show.rings?.filter(r => r.ring === ringName) || []
    : show.rings || [];


  const judgeEntries: IJudgeScheduleEntry[] = [];

  // Every judge's running_order carries a "placeholder" dog_class row (0 entries) for
  // EVERY possible element/level the show offers, not just the ones they actually judge
  // (see below). We need the full set of judge names in this show up front so the
  // nosework/scentwork rotation fallback (further down) can tell "no judge in this show
  // has explicitly claimed this class via score.judge_name" apart from "another judge in
  // this show has already explicitly claimed it" — otherwise every OTHER judge's empty
  // placeholder row for that same level/element silently absorbs the same entries too.
  const allJudgeNamesInShow = new Set<string>();
  targetRings.forEach(ring => {
    (ring.running_order || []).forEach(ro => {
      const n = (ro as any).judge_name?.trim();
      if (n) allJudgeNamesInShow.add(n);
    });
  });

  targetRings.forEach(ring => {
    if (!ring.running_order) {
      return;
    }

    ring.running_order.forEach((runningOrder, index) => {
      
      
      const judgeEntry: IJudgeScheduleEntry = {
        judgeName: runningOrder.judge_name || 'Unknown Judge',
        startTime: (runningOrder as any).start_time || '8:00 AM', // Type assertion for extended property
        dogClasses: [],
        totalEntries: 0,
        totalMinutes: 0,
        judgingMinutes: 0 // Initialize the new field
      };

      // Process each dog class for this judge
      runningOrder.dog_classes?.forEach((dogClass, dcIndex) => {
        const dcWithExtended = dogClass as any; // Type assertion for extended properties
        const classShowId = (dcWithExtended.concurrent_show_id || show._id)?.toString();
        
        // Debug: Log break detection
        if (dogClass.level && (dogClass.level.toLowerCase().includes('break') || dogClass.level.toLowerCase().includes('lunch'))) {
          // no-op: kept for clarity when reviewing break handling
        }
        
        // For breaks, use duration directly
        const correctMinutes = dogClass.type === Constants.running_order_dog_class_type.break
          ? (dogClass.duration || 0)
          : (dogClass.total_minutes || 0);
        
        
        // Get actual entry count from scores when available
        let actualEntries = dogClass.entries || 0;
        let actualMinutes = correctMinutes;
        
        if (dogClass.type !== Constants.running_order_dog_class_type.break) {
          const durationPerEntry = event
            ? getDurationPerEntry(event, show, dogClass, dogClass.duration || 0)
            : (dogClass.duration || 0);

          // For split tiles (split_number is set), score-based counting is unreliable:
          // all scores for the class share the same level/element/judge so we can't
          // distinguish split 1 from split 2 at the score level. Use dog_run_order
          // length (most accurate) or saved entries instead.
          const isSplitTile = dcWithExtended.split_number !== undefined;
          if (isSplitTile) {
            const dogRunOrder = dcWithExtended.dog_run_order || [];
            actualEntries = dogRunOrder.length > 0
              ? dogRunOrder.length
              : (dogClass.entries || 0);
          } else if (scores) {
            // Filter scores by show + level + element + section + judge_name.
            // Filtering by judge_name (when present on both sides) ensures each ring only
            // counts the dogs assigned to that judge, which is always accurate even when
            // new registrations arrive after the last sync/reset (dog_run_order can be stale).
            // Filtering by show_id ensures correctness when allScores spans multiple shows.
            const judgeName = (runningOrder as any).judge_name?.trim();
            // AKC Conformation tiles may carry a legacy element label ("Conformation" vs
            // "Non-Licensed Classes") and may be split by sex / variation - match accordingly.
            const isAKCConformation =
              event?.sanctioning_club === Constants.sanctioning_club.AKC &&
              show.show_type === Constants.show_type.conformation;
            const matchingScores = scores.filter(score => {
              // Waitlisted AKC Herding "Alternates" (synthetic, is_alternate:
              // true) are display-only additions merged in purely for
              // print/export rendering - they aren't actually running, so they
              // must never inflate entry counts / judging duration. See
              // data/prd/AKC_HERDING_ALTERNATES_PRD.md, Section 3.3.
              if (score.is_alternate) return false;
              const showMatch = score.show_id?.toString() === classShowId;
              const levelMatch = score.dog_class?.level === dogClass.level;

              const elementMatch = dogClass.show_element
                ? (isAKCConformation
                    ? akcConformationClassMatches(dogClass, score.dog_class || {})
                    : score.dog_class?.show_element === dogClass.show_element)
                : true;

              const dcSection = (dogClass as any).section;
              const sectionMatch = dcSection
                ? score.dog_class?.section === dcSection
                : true;

              const sexMatch = dcWithExtended.sex_split === "Dogs"
                ? score.dog_sex === Constants.dog_sex.male
                : dcWithExtended.sex_split === "Bitches"
                  ? score.dog_sex === Constants.dog_sex.female
                  : true;
              const variationMatch = dcWithExtended.variation
                ? (score.variation || "").trim() === String(dcWithExtended.variation).trim()
                : true;

              // Use judge_name for accuracy when available on both the ring entry and the score.
              // If either is missing (legacy data), skip the judge filter so entries still show.
              const judgeMatch = (judgeName && score.judge_name)
                ? score.judge_name?.trim() === judgeName
                : true;

              return showMatch && levelMatch && elementMatch && sectionMatch && sexMatch && variationMatch && judgeMatch;
            });

            if (matchingScores.length > 0) {
              // Live score data matched — always most accurate (handles new registrations)
              actualEntries = matchingScores.length;
            } else if (dogClass.show_element && [
              Constants.dog_class_element.nosework_container,
              Constants.dog_class_element.nosework_exterior,
              Constants.dog_class_element.nosework_interior,
              Constants.dog_class_element.nosework_vehicle,
              Constants.dog_class_element.scentwork_container,
              Constants.dog_class_element.scentwork_interior,
              Constants.dog_class_element.scentwork_exterior,
              Constants.dog_class_element.scentwork_buried,
              Constants.dog_class_element.scentwork_handler_disc,
              Constants.dog_class_element.scentwork_detective,
            ].includes(dogClass.show_element as string)) {
              // No judge_name match for a nosework/scentwork rotation element.
              // In rotation format, a judge is assigned to an element in the ring but scores
              // may carry a different judge_name (e.g. element-level split across judges).
              // Use the live element+level count (ignoring judge_name) so the entry count
              // stays current even when the ring data is stale.
              const elementScores = scores.filter(score => {
                if (score.is_alternate) return false;
                const showMatch = score.show_id?.toString() === classShowId;
                const levelMatch = score.dog_class?.level === dogClass.level;
                const elementMatch = score.dog_class?.show_element === dogClass.show_element;
                const dcSection = (dogClass as any).section;
                const sectionMatch = dcSection
                  ? score.dog_class?.section === dcSection
                  : true;
                return showMatch && levelMatch && elementMatch && sectionMatch;
              });

              // Guard against double-counting on multi-judge shows: if these scores'
              // judge_name unambiguously identifies a DIFFERENT judge who is also
              // scheduled in this show, this class truly belongs to that other judge —
              // not to this judge's empty placeholder row for the same level/element.
              // Without this check, every judge who *doesn't* judge this level/element
              // still has a 0-entry placeholder row for it, and each one independently
              // falls into this same "ignore judge_name" branch and re-counts the exact
              // same entries, inflating every other judge's schedule/hours by the sum of
              // every level they don't actually judge (see the "judging minutes don't
              // match the tiles below" investigation for event 8ehorr).
              const claimedByAnotherJudge = elementScores.some(score => {
                const scoreJudge = score.judge_name?.trim();
                return (
                  !!scoreJudge &&
                  scoreJudge !== judgeName &&
                  allJudgeNamesInShow.has(scoreJudge)
                );
              });

              // Trust live data: if all entries were moved to waitlist (scores deleted),
              // the count will be 0 rather than falling back to stale ring data.
              actualEntries = claimedByAnotherJudge ? 0 : elementScores.length;
            } else {
              // No score match for this judge + class combination.
              // When live score data is provided, trust it — a count of 0 means the class
              // is truly empty (e.g. all entries moved to waitlist with scores deleted).
              // Only fall back to ring-stored count when no score data was passed in.
              actualEntries = scores ? 0 : (dogClass.entries || 0);
            }
          } else {
            const dogRunOrder = (dogClass as any).dog_run_order || [];
            if (dogRunOrder.length > 0) {
              actualEntries = dogRunOrder.length;
            }
          }

          actualMinutes = durationPerEntry > 0 ? actualEntries * durationPerEntry : correctMinutes;

        }

        // Waitlisted AKC Herding "Alternates" (is_alternate: true) are excluded
        // from actualEntries/actualMinutes above (they don't run, so they can't
        // count toward duration), but a class made up entirely of alternates
        // (e.g. every real entry withdrew, or none registered yet) must still
        // get a tile in the schedule so those alternates are visible in the
        // export - otherwise the whole class silently disappears. See
        // data/prd/AKC_HERDING_ALTERNATES_PRD.md, Section 3.3.
        const hasAlternates = !!scores?.some(score => {
          if (!score.is_alternate) return false;
          const showMatch = score.show_id?.toString() === classShowId;
          const levelMatch = score.dog_class?.level === dogClass.level;
          const elementMatch = dogClass.show_element
            ? score.dog_class?.show_element === dogClass.show_element
            : true;
          const dcSection = (dogClass as any).section;
          const sectionMatch = dcSection
            ? score.dog_class?.section === dcSection
            : true;
          return showMatch && levelMatch && elementMatch && sectionMatch;
        });

        // Only add classes that have entries, alternates, or are breaks
        if (actualEntries > 0 || hasAlternates || dogClass.type === Constants.running_order_dog_class_type.break) {
          const token = dogClass.type === Constants.running_order_dog_class_type.break ? "" : (dcWithExtended.concurrent_token || "");
          const rawLevel = dogClass.level || dcWithExtended.name || 'Unknown Level';
          const rawLabel = dcWithExtended.label || dcWithExtended.name;
          judgeEntry.dogClasses.push({
            level: token ? `${token} - ${rawLevel}` : rawLevel,
            entries: actualEntries,
            totalMinutes: actualMinutes,
            maleCount: dcWithExtended.male_count,
            femaleCount: dcWithExtended.female_count,
            label: token ? `${token} - ${rawLabel || rawLevel}` : rawLabel, // This includes grouped class labels and break names
            group_with: dogClass.group_with, // Preserve the group_with property for grouped child classes
            show_element: dogClass.show_element, // Preserve show_element for scentwork
            section: dcWithExtended.section, // Preserve section for C-WAGS Obedience/Rally and UKC Nosework
            isBreak: dogClass.type === Constants.running_order_dog_class_type.break // Flag breaks
          });
          
          // Debug: Log if this is a break
          if (dogClass.type === Constants.running_order_dog_class_type.break) {
          }
        }

        // Only include non-grouped, non-break classes in totals to avoid double-counting
        // Breaks should not count towards judge hours
        if (dogClass.type !== Constants.running_order_dog_class_type.break && dogClass.group_with === undefined) {
          judgeEntry.totalEntries += actualEntries;
          judgeEntry.totalMinutes += actualMinutes;
        }
      });

      // Post-process to merge grouped classes
      const processedClasses: typeof judgeEntry.dogClasses = [];
      const groupedClassesMap = new Map<string, typeof judgeEntry.dogClasses[0]>();

      judgeEntry.dogClasses.forEach((dc, idx) => {
      });

      // FIRST PASS: Add all parent/standalone classes to the map
      judgeEntry.dogClasses.forEach(dogClass => {
        if (!dogClass.group_with) {
          // This is a parent class or standalone class
          groupedClassesMap.set(dogClass.level, dogClass);
          processedClasses.push(dogClass);
        } else {
        }
      });

      // SECOND PASS: Merge grouped children into their parents
      judgeEntry.dogClasses.forEach(dogClass => {
        if (dogClass.group_with) {
          
          // This is a grouped child class - merge it with its parent
          // Support both formats: "Show Element:Level" and just "Level"
          let parentClass = groupedClassesMap.get(dogClass.group_with);
          
          
          // If not found and group_with contains ":", try extracting just the level part
          if (!parentClass && dogClass.group_with.includes(':')) {
            const levelOnly = dogClass.group_with.split(':')[1];
            parentClass = groupedClassesMap.get(levelOnly);
          }
          
          // If still not found and group_with doesn't contain ":", try prefixing with show_element
          if (!parentClass && !dogClass.group_with.includes(':') && dogClass.show_element) {
            const fullIdentifier = `${dogClass.show_element}:${dogClass.group_with}`;
            parentClass = groupedClassesMap.get(fullIdentifier);
          }
          
          if (parentClass) {
            // Merge the child into the parent
            parentClass.entries += dogClass.entries;
            parentClass.totalMinutes += dogClass.totalMinutes;
            parentClass.label = parentClass.label 
              ? `${parentClass.level}/${dogClass.level}` 
              : `${parentClass.level}/${dogClass.level}`;
            // Add child's male/female counts to parent
            if (dogClass.maleCount) parentClass.maleCount = (parentClass.maleCount || 0) + dogClass.maleCount;
            if (dogClass.femaleCount) parentClass.femaleCount = (parentClass.femaleCount || 0) + dogClass.femaleCount;
          } else {
          }
        }
      });

      judgeEntry.dogClasses = processedClasses;

      // Recalculate totals after merging grouped classes
      judgeEntry.totalEntries = processedClasses.reduce((sum, dogClass) => sum + dogClass.entries, 0);
      judgeEntry.totalMinutes = processedClasses.reduce((sum, dogClass) => sum + dogClass.totalMinutes, 0);
      
      // Calculate judging minutes (excluding breaks)
      judgeEntry.judgingMinutes = processedClasses.reduce((sum, dogClass) => {
        if (dogClass.isBreak) {
          return sum; // Exclude breaks
        }
        return sum + dogClass.totalMinutes;
      }, 0);


      judgeEntries.push(judgeEntry);
    });
  });

  return judgeEntries;
}

/**
 * Format judge schedule entry for display
 */
export function formatJudgeScheduleEntry(
  dogClass: IJudgeScheduleEntry['dogClasses'][0],
  includeGenderCounts: boolean = false
): string {
  const displayLevel = dogClass.label || dogClass.level;
  
  if (includeGenderCounts && dogClass.maleCount !== undefined && dogClass.femaleCount !== undefined) {
    return `${dogClass.entries} - ${displayLevel} - ${dogClass.maleCount} - ${dogClass.femaleCount}`;
  }
  
  return `${dogClass.entries} - ${displayLevel}`;
}

/**
 * Calculate total time for a judge including breaks
 */
export function calculateJudgeTotalTime(judgeEntry: IJudgeScheduleEntry): string {
  const totalMinutes = judgeEntry.totalMinutes;
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

/**
 * Sorts shows chronologically for judging schedule grouping (date/ring headers,
 * event summary ordering, etc.), but treats a trailing show (trailing_show +
 * previous_show_name/previous_show_id) as strictly coming after the show it
 * follows.
 *
 * Trailing shows are frequently left with a stale/placeholder show_date from
 * before the trailing link was configured — their real estimated start time is
 * derived dynamically from the previous show's duration (see
 * calculateClassStartTime), not from their own stored show_date. A plain
 * chronological sort by show_date alone can therefore place a trailing show
 * (e.g. "Trial 2") before the show it's supposed to follow (e.g. "Trial 1") in
 * every judging schedule export across every sanctioning club builder.
 *
 * This performs a stable topological sort: shows are ordered chronologically
 * by default, but any show that resolves to a previous show within the same
 * list is guaranteed to be placed after it.
 */
export function sortShowsRespectingTrailingLinks<T extends IShow>(
  shows: T[]
): T[] {
  const baseline = [...shows].sort(
    (a, b) => new Date(a.show_date).getTime() - new Date(b.show_date).getTime()
  );

  const resolvePreviousShow = (show: T): T | undefined => {
    if (!show.trailing_show) return undefined;
    if (show.previous_show_id) {
      const byId = baseline.find(
        (s) => s._id?.toString() === (show.previous_show_id as any)?.toString()
      );
      if (byId) return byId;
    }
    if (!show.previous_show_name) return undefined;
    return (
      baseline.find((s) => s.show_name === show.previous_show_name) ??
      baseline.find(
        (s) => `${s.show_type} ${s.show_name}` === show.previous_show_name
      )
    );
  };

  const result: T[] = [];
  const placed = new Set<string>();
  const remaining = [...baseline];

  while (remaining.length > 0) {
    const idx = remaining.findIndex((show) => {
      const prev = resolvePreviousShow(show);
      if (!prev) return true;
      const prevId = prev._id?.toString();
      const showId = show._id?.toString();
      // Guard against a show somehow referencing itself as its own previous show.
      return prevId === showId || placed.has(prevId || "");
    });

    // Fallback: if every remaining show is blocked (e.g. a broken/circular
    // previous-show reference), take the next one anyway to avoid hanging.
    const takeIdx = idx === -1 ? 0 : idx;
    const [show] = remaining.splice(takeIdx, 1);
    result.push(show);
    placed.add(show._id?.toString() || "");
  }

  return result;
}

/**
 * Ceil a clock time to the next 5-minute mark in the given timezone.
 * 9:00 stays 9:00; 9:01–9:04 become 9:05. Adds a duration to the Date so
 * later timezone formatting stays correct (including DST).
 */
export function ceilToFiveMinuteInterval(date: Date, timezone?: string): Date {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const minutePart = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    minute: "2-digit",
    hour12: false,
  })
    .formatToParts(date)
    .find((part) => part.type === "minute");
  const minute = Number(minutePart?.value ?? 0);
  const remainder = minute % 5;
  if (remainder === 0) {
    return new Date(date.getTime());
  }
  const rounded = new Date(date.getTime());
  rounded.setMinutes(rounded.getMinutes() + (5 - remainder));
  return rounded;
}

/**
 * Format a class/judge start time the way judging-schedule exports do.
 * When `roundToFive` is true, only the displayed clock is ceiled — the
 * incoming Date is not mutated and callers must not feed the rounded
 * value back into the cumulative schedule clock.
 */
/** Display-only: return a ceiled copy when the show has rounding enabled. */
export function displayJudgeStartDate(
  date: Date,
  show?: Pick<IShow, "round_start_times_to_5">,
  timezone?: string
): Date {
  return show?.round_start_times_to_5
    ? ceilToFiveMinuteInterval(date, timezone)
    : date;
}

export function formatJudgeStartTime(
  date: Date,
  timezone?: string,
  roundToFive?: boolean
): string {
  const tz = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const display = roundToFive ? ceilToFiveMinuteInterval(date, tz) : date;
  return display
    .toLocaleString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: tz,
    })
    .replace(/,.*/, "")
    .toUpperCase();
}

/**
 * Calculate the start time for a specific class within a show
 * This ensures consistent timing calculations between Judge Schedule tab and export
 */
export function calculateClassStartTime(
  show: IShow,
  ringName: string,
  judgeIndex: number,
  classIndex: number,
  timezone?: string,
  allShows?: IShow[],
  scores?: any[],
  event?: IEvent
): string {
  
  const judgeScheduleEntries = extractJudgeScheduleFromRings(show, ringName, scores, event);
  
  
  let totalMinutes = 0;
  let baseStartTime = new Date(show.show_date);
  
  
  // Handle trailing shows - if this show follows another show, start after the previous show ends
  if (show.trailing_show && show.previous_show_name) {
    if (allShows) {
      // Try exact match first, then show_type + show_name combination
      let previousShow = allShows.find(s => s.show_name === show.previous_show_name);
      if (!previousShow && show.previous_show_name) {
        // Try to find by show_type + show_name combination (e.g., "Obedience" + "Trial 1" = "Obedience Trial 1")
        previousShow = allShows.find(s => {
          const combinedName = `${s.show_type} ${s.show_name}`;
          return combinedName === show.previous_show_name;
        });
      }
      
      if (previousShow) {
        // Calculate the total duration of the previous show
        const previousShowEntries = extractJudgeScheduleFromRings(previousShow, ringName, scores, event);
        const previousShowTotalMinutes = previousShowEntries.reduce((sum, entry) => sum + entry.totalMinutes, 0);
        
        // Start this show immediately after the previous show ends
        baseStartTime = new Date(previousShow.show_date);
        baseStartTime.setMinutes(baseStartTime.getMinutes() + previousShowTotalMinutes + ringStartOffsetMinutes(previousShow, ringName));
      }
    }
  }

  baseStartTime.setMinutes(baseStartTime.getMinutes() + ringStartOffsetMinutes(show, ringName));
  
  // Add time from all previous judges
  for (let j = 0; j < judgeIndex && j < judgeScheduleEntries.length; j++) {
    totalMinutes += judgeScheduleEntries[j].totalMinutes;
  }
  
  // Add time from previous classes in current judge
  if (judgeIndex < judgeScheduleEntries.length) {
    const currentJudge = judgeScheduleEntries[judgeIndex];
    
    for (let c = 0; c < classIndex && c < currentJudge.dogClasses.length; c++) {
      const dogClass = currentJudge.dogClasses[c];
      
      // All classes in the processed list should be counted (grouped children have been merged)
      // This includes breaks, as we need them for calculating accurate start times
      totalMinutes += dogClass.totalMinutes;
    }
  }
  
  const startTime = new Date(baseStartTime);
  startTime.setMinutes(startTime.getMinutes() + totalMinutes);

  return formatJudgeStartTime(
    startTime,
    timezone,
    !!show.round_start_times_to_5
  );
}
